// src/components/ui/Select.tsx
'use client';

import React, { forwardRef, SelectHTMLAttributes } from 'react';
import { cva, type VariantProps } from 'class-variance-authority';
import { cn } from '@/lib/utils';
import { ChevronDownIcon } from '@heroicons/react/24/outline';

const selectVariants = cva(
  'flex w-full appearance-none rounded-md border bg-background px-3 py-2 text-sm ring-offset-background transition-all duration-150 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50',
  {
    variants: {
      size: {
        sm: 'h-9 px-3 text-sm',
        md: 'h-11 px-4 text-sm',
        lg: 'h-12 px-4 text-base',
      },
      variant: {
        default: 'border-input',
        error: 'border-destructive focus-visible:ring-destructive',
        success: 'border-success focus-visible:ring-success',
      },
    },
    defaultVariants: {
      size: 'md',
      variant: 'default',
    },
  }
);

export interface SelectProps
  extends SelectHTMLAttributes<HTMLSelectElement>,
    VariantProps<typeof selectVariants> {
  error?: string;
  helperText?: string;
}

const Select = forwardRef<HTMLSelectElement, SelectProps>(
  ({ className, size, variant, error, helperText, children, ...props }, ref) => {
    const selectVariant = error ? 'error' : variant;

    return (
      <div className="w-full">
        <div className="relative">
          <select
            className={cn(selectVariants({ size, variant: selectVariant, className }), 'pr-10')}
            ref={ref}
            {...props}
          >
            {children}
          </select>
          <ChevronDownIcon
            className="absolute right-3 top-1/2 -translate-y-1/2 h-5 w-5 text-muted-foreground pointer-events-none"
            aria-hidden="true"
          />
        </div>
        {error && <p className="mt-1.5 text-xs text-destructive">{error}</p>}
        {helperText && !error && <p className="mt-1.5 text-xs text-muted-foreground">{helperText}</p>}
      </div>
    );
  }
);

Select.displayName = 'Select';

export { Select, selectVariants };

